import { useCompanyContext } from "@/features/company/hooks/use-company-context";
import { identityPartners, MandateServiceType, UserType } from "../types";
import { useOrigin } from "@/features/flow/hooks/use-origin";
import { FromURL } from "@/features/flow/types";
import { useAppSelector } from "@/redux/hooks";
import { companyDetails, MandateService } from "@/features/company/types";
import { getUserRoles } from "@/redux/slices/userSlice";
import { role } from "@/features/auth/types";
import { useUser } from "./use-user";
import { useCompany } from "@/features/company/hooks/use-company";
import { usePartnerInfo } from "@/features/partner/hooks/use-partner-information";

export const useUserCheckAccess = () => {
  const { leUuid } = useCompanyContext();
  const origin = useOrigin();
  const isPartner = origin === FromURL.Partner;
  const listeCompanies = useAppSelector((state) => state.user.linked_companies);
  const userRoles = useAppSelector(getUserRoles);
  const isAdmin = (() => userRoles.includes(role.ADMIN))();

  const checkPermission = (
    serviceAction?: MandateServiceType | null,
    user?: UserType | null,
    companyDetails?: companyDetails | null,
    identityPartner?: identityPartners | null,
    companyInfoActions?: boolean | null
  ): boolean => {
    if (isAdmin) return true;

    if (serviceAction === MandateService.ALIASES_MANAGEMENT) {
      companyInfoActions = true;
    }

    if (!leUuid) {
      //ALIASES MANAGEMENT  + userExist we return true
      if (!user && !identityPartner && serviceAction === MandateService.ALIASES_MANAGEMENT)
        return true;
      //withdraw + userExist we return true
      if (!!user && serviceAction === MandateService.KYPAY_WITHDRAW) return true;

      //we check the kyc level otherwise
      const kycLevel = user?.olkyPassLevel?.olkypass_status?.level;
      //const kycLevelUnderAudit = user?.olkyPassLevel?.olkypass_status_under_audit?.level;

      // check if user is under audit
      //if (kycLevelUnderAudit && kycLevel && kycLevelUnderAudit > kycLevel) return false;

      if (!isPartner) {
        return !!(kycLevel && kycLevel >= 2);
      } else {
        const minLvlrequired = identityPartner?.minimum_olkypass_level ?? 2;
        return !!(kycLevel && kycLevel >= minLvlrequired);
      }
    } else {
      //if company details is null we return false
      if (!companyDetails) return false;
      //if is wating for doc consent or company validation is pending
      if (
        companyDetails?.legalEntity?.IsWaitingConsent ||
        (!companyDetails?.legalEntity?.AgentReviewFinish &&
          !!companyDetails?.legalEntity?.UserValidation) ||
        (companyInfoActions && companyDetails?.legalEntity?.AgentReviewFinish)
      )
        return false;
      const userKycLevel = user?.olkyPassLevel?.olkypass_status?.level;
      // const kycLevelUnderAudit = user?.olkyPassLevel?.olkypass_status_under_audit?.level;

      //here we check if the user authenticated is lvl 2 or more
      let userIsAuthorized = true;
      if (!isPartner) {
        if (userKycLevel && userKycLevel < 2) userIsAuthorized = false;
      } else {
        const minLvlrequired = identityPartner?.minimum_olkypass_level ?? 2;
        if (!!userKycLevel && userKycLevel < minLvlrequired) {
          userIsAuthorized = false;
        }
      }

      //here we check only the user level in the company because there is no service action or need ti check the company level
      if (companyInfoActions) return userIsAuthorized;
      // Is the action is withdraw (normaly true for everyone except in a company where the legal mandate dosent have the right)
      if (serviceAction === MandateService.KYPAY_WITHDRAW)
        return (
          !!listeCompanies[companyDetails.legalEntity.UUID].mandate_services?.includes(
            serviceAction
          ) && userIsAuthorized
        );

      const kycLevel = companyDetails?.olkyPassLevel.olkypass_status?.level;
      //const kycLevelUnderAudit = companyDetails?.olkyPassLevel?.olkypass_status_under_audit?.level;
      //actionPermited is the check of the legal mandate service mandate (role)
      const actionPermited =
        !!serviceAction &&
        !!listeCompanies[companyDetails.legalEntity.UUID].mandate_services?.includes(serviceAction);

      // now if its a partner we check le level min and the action (alias email here ) permitted  otherwise  we check only the level (feed bank , send kypay on chain etc ...)(if no service action thats the kypay actions)
      //we check the  company kyc  level
      if (!isPartner) {
        const result = !serviceAction
          ? !!(kycLevel && kycLevel >= 2)
          : !!(
              kycLevel &&
              kycLevel < 2 &&
              serviceAction === MandateService.ALIASES_MANAGEMENT &&
              actionPermited
            );
        return result && userIsAuthorized;
      } else {
        const minLvlrequired = identityPartner?.minimum_olkypass_pro_level ?? 2;
        return !serviceAction
          ? !!kycLevel && kycLevel >= minLvlrequired && userIsAuthorized
          : !!(
              kycLevel &&
              kycLevel < minLvlrequired &&
              serviceAction === MandateService.ALIASES_MANAGEMENT &&
              actionPermited
            ) && userIsAuthorized;
      }
    }
  };

  const checkCompanyInfoActionsPermission = (permission: MandateService | null = null) => {
    const { useUserProfile } = useUser();
    const { data: user } = useUserProfile();
    const { leUuid } = useCompanyContext();
    const { companyDetailQuery } = useCompany();
    const { data: companyDetails } = companyDetailQuery(leUuid as string);
    const { getCurrentIdentityPartner } = usePartnerInfo();
    const identityPartner = getCurrentIdentityPartner();

    return checkPermission(permission, user, companyDetails, identityPartner, true);
  };

  return { checkPermission, checkCompanyInfoActionsPermission };
};
